import React from 'react';
import { motion } from 'framer-motion';
import { Globe, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();

  const availableLanguages = [
    { code: 'pt-BR', name: t('languages.pt-BR') },
    { code: 'es', name: t('languages.es') },
    { code: 'en', name: t('languages.en') },
    { code: 'fr', name: t('languages.fr') },
  ];

  const currentLanguage = i18n.language;

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="p-6 lg:p-10 max-w-4xl mx-auto flex flex-col gap-8"
      >
        <motion.h1
          className="text-4xl font-black leading-tight tracking-[-0.033em] text-transparent bg-clip-text bg-gradient-primary"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
        >
          {t('settings.title')}
        </motion.h1>

        <div className="card p-6 flex flex-col gap-6">

          {/* Seção de Idioma */}
          <h2 className="text-xl font-bold text-[#181411] dark:text-white flex items-center gap-2 border-b border-gray-200 dark:border-gray-700/50 pb-3">
            <Globe className="size-6 text-primary" /> {t('settings.language_title')}
          </h2>

          <div className="flex flex-col gap-4">
            <div>
              <p className="font-medium text-[#181411] dark:text-white">{t('settings.language_title')}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('settings.language_description')}</p>
            </div>

            <div className="flex flex-wrap gap-3">
              {availableLanguages.map((lang) => (
                <motion.button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${currentLanguage === lang.code
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {lang.name}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        {/* Disclaimer Card */}
        <motion.div
          className="card p-6 flex gap-3 items-start bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/50"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <AlertTriangle className="size-5 text-amber-600 dark:text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100 mb-2">
              {t('disclaimers.whatsapp_blocking_title')}
            </h3>
            <p className="text-sm text-amber-800 dark:text-amber-200 leading-relaxed">
              {t('disclaimers.whatsapp_blocking_message')}
            </p>
          </div>
        </motion.div>
      </motion.div>
    </>
  );
};

export default SettingsPage;